import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import type { components } from "../schema";

export type WorldlineNode = components["schemas"]["WorldlineNode"];

export type UseWorldlineTreeResult = UseQueryResult<WorldlineNode[], ApiError>;

// GET /scenarios/{scenarioId}/worldlines を呼び、世界線ツリー（WorldlineNode 配列）を返す query。
// scenarioId が null の場合はクエリ自体を無効化する。
// 世界線ツリーは分岐操作のたびに変化するため staleTime は設定せず、
// デフォルトの再取得タイミングに委ねる。
export function useWorldlineTree(
  scenarioId: string | null,
): UseWorldlineTreeResult {
  const client = useApiClient();

  return useQuery<WorldlineNode[], ApiError>({
    queryKey: ["worldlineTree", scenarioId],
    enabled: scenarioId !== null,
    queryFn: async () => {
      // enabled: false の場合は呼ばれないが、型を狭めるための保険的なガード。
      if (scenarioId === null) {
        throw new ApiError(0, {
          type: "about:blank",
          title: "Invalid state",
          status: 0,
          detail: "useWorldlineTree queryFn invoked without scenarioId",
        });
      }

      const { data, error } = await client.GET(
        "/scenarios/{scenarioId}/worldlines",
        {
          params: { path: { scenarioId } },
        },
      );

      if (error !== undefined) {
        throw new ApiError(
          typeof (error as { status?: number }).status === "number"
            ? (error as { status: number }).status
            : 0,
          error as unknown as ApiError["problem"],
        );
      }
      if (data === undefined) {
        throw new ApiError(500, {
          type: "about:blank",
          title: "Empty response",
          status: 500,
          detail: "getWorldlineTree returned no data",
        });
      }
      return data;
    },
  });
}
